import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api, formatPrice } from '@/lib/api';
import { toast } from 'sonner';
import { Shield, Check, X, Package, Users, MessageSquare, Star, Plus, Edit, Trash2, Mail, Settings as SettingsIcon, FileText, Car, KeyRound, CreditCard, Save, Wrench } from 'lucide-react';
import { ImageInput } from '@/components/ImageInput';
import { FitmentEditor } from '@/components/FitmentEditor';

const TABS = [
    { key: 'reviews', label: 'Reviews', icon: Star },
    { key: 'products', label: 'Products', icon: Package },
    { key: 'orders', label: 'Orders', icon: MessageSquare },
    { key: 'members', label: 'Members', icon: Users },
    { key: 'newsletter', label: 'Newsletter', icon: Mail },
    { key: 'site', label: 'Site Content', icon: FileText },
    { key: 'vehicles', label: 'Vehicles', icon: Car },
    { key: 'settings', label: 'Settings', icon: SettingsIcon },
];

export default function Admin() {
    const { user, loading } = useAuth();
    const [tab, setTab] = useState('reviews');

    if (loading) return <div className="max-w-7xl mx-auto p-8 text-white/50">Loading…</div>;
    if (!user) return <Navigate to="/admin/login" replace />;
    if (!user.is_admin) return <Navigate to="/" replace />;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center gap-3 mb-6">
                <Shield className="w-6 h-6 text-amber-400" />
                <h1 className="font-display text-4xl text-white">ADMIN DASHBOARD</h1>
            </div>
            <div className="flex flex-wrap gap-1 border-b border-white/10 mb-6" data-testid="admin-dashboard-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`admin-tab-${t.key}`}
                        className={`px-4 py-2 text-sm inline-flex items-center gap-2 border-b-2 -mb-px ${
                            tab === t.key ? 'border-amber-400 text-white' : 'border-transparent text-white/60 hover:text-white'
                        }`}
                    >
                        <t.icon className="w-4 h-4" /> {t.label}
                    </button>
                ))}
            </div>
            {tab === 'reviews' && <ReviewsTab />}
            {tab === 'products' && <ProductsTab />}
            {tab === 'orders' && <OrdersTab />}
            {tab === 'members' && <MembersTab />}
            {tab === 'newsletter' && <NewsletterTab />}
            {tab === 'site' && <SiteContentTab />}
            {tab === 'vehicles' && <VehiclesTab />}
            {tab === 'settings' && <SettingsTab />}
        </div>
    );
}

function ReviewsTab() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState('all');
    const load = () => api.get('/reviews/all').then(({ data }) => setItems(data.items || []));
    useEffect(() => { load(); }, []);
    const filtered = items.filter((r) => filter === 'all' || r.status === filter);

    const action = async (id, kind) => {
        try {
            await api.patch(`/reviews/${id}/${kind}`);
            toast.success(`Review ${kind}d`);
            load();
        } catch {
            toast.error('Failed');
        }
    };

    return (
        <div>
            <div className="flex items-center gap-2 mb-4">
                {['all', 'pending', 'approved', 'rejected'].map((f) => (
                    <button key={f} onClick={() => setFilter(f)} className={`text-xs px-3 py-1 rounded-full border ${filter === f ? 'bg-amber-500/20 border-amber-500/40 text-amber-200' : 'border-white/10 text-white/60'}`}>
                        {f.toUpperCase()}
                    </button>
                ))}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((r) => (
                    <div key={r.id} className="rrm-panel p-4">
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-widest text-white/40">{r.status}</div>
                            <div className="flex items-center gap-1 text-amber-400">
                                {Array.from({ length: r.rating }).map((_, i) => <Star key={`${r.id}-star-${i}`} className="w-3.5 h-3.5 fill-current" />)}
                            </div>
                        </div>
                        {r.title && <div className="mt-2 font-semibold text-white">{r.title}</div>}
                        <p className="mt-2 text-sm text-white/70">{r.body}</p>
                        <div className="mt-3 text-xs text-white/50">— {r.name} {r.vehicle ? `· ${r.vehicle}` : ''}</div>
                        {r.status !== 'approved' && (
                            <div className="mt-3 flex gap-2">
                                <button onClick={() => action(r.id, 'approve')} data-testid="admin-approve-review-button" className="text-xs px-3 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 inline-flex items-center gap-1">
                                    <Check className="w-3.5 h-3.5" /> Approve
                                </button>
                                {r.status !== 'rejected' && (
                                    <button onClick={() => action(r.id, 'reject')} data-testid="admin-reject-review-button" className="text-xs px-3 py-1.5 rounded-md bg-rose-500/15 border border-rose-500/30 text-rose-200 inline-flex items-center gap-1">
                                        <X className="w-3.5 h-3.5" /> Reject
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ))}
                {filtered.length === 0 && <div className="text-white/50 text-sm">No reviews in this filter.</div>}
            </div>
        </div>
    );
}

function ProductsTab() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [quickImage, setQuickImage] = useState(null); // {id, name, url}
    const [quickFitment, setQuickFitment] = useState(null); // {id, name, compatibility}

    const load = () => api.get('/products?limit=200').then(({ data }) => setItems(data.items || []));
    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!confirm('Delete this product?')) return;
        try {
            await api.delete(`/products/${id}`);
            toast.success('Deleted');
            load();
        } catch {
            toast.error('Failed');
        }
    };

    const saveQuickImage = async () => {
        if (!quickImage) return;
        try {
            await api.patch(`/products/${quickImage.id}`, { image_url: quickImage.url });
            toast.success('Image updated');
            setQuickImage(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        }
    };

    const saveQuickFitment = async () => {
        if (!quickFitment) return;
        try {
            await api.patch(`/products/${quickFitment.id}`, { compatibility: quickFitment.compatibility || [] });
            toast.success('Fitment updated');
            setQuickFitment(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-white/60">{items.length} products in catalog</div>
                <button
                    onClick={() => { setEditing(null); setShowForm(true); }}
                    data-testid="admin-product-new"
                    className="text-sm px-3 py-1.5 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white inline-flex items-center gap-1.5"
                >
                    <Plus className="w-4 h-4" /> New product
                </button>
            </div>
            <div className="rrm-panel overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-left text-white/50 text-xs uppercase tracking-widest">
                        <tr className="border-b border-white/10">
                            <th className="p-3">Image</th>
                            <th className="p-3">Name</th>
                            <th className="p-3">Brand</th>
                            <th className="p-3">Category</th>
                            <th className="p-3">Type</th>
                            <th className="p-3">Price</th>
                            <th className="p-3">Stock</th>
                            <th className="p-3">Fitment</th>
                            <th className="p-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.id} className="border-b border-white/5">
                                <td className="p-3">
                                    <button
                                        onClick={() => setQuickImage({ id: p.id, name: p.name, url: p.image_url })}
                                        title="Quick edit image"
                                        data-testid="admin-product-quick-image"
                                        className="group relative block"
                                    >
                                        <img src={p.image_url} alt={p.name} className="w-14 h-14 rounded-md object-cover bg-black/40 border border-white/10 group-hover:border-amber-400/60" />
                                        <span className="absolute inset-0 inline-flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 rounded-md text-[10px] text-amber-300 transition-opacity">
                                            EDIT
                                        </span>
                                    </button>
                                </td>
                                <td className="p-3 text-white max-w-[260px]">{p.name}</td>
                                <td className="p-3 text-white/70">{p.brand}</td>
                                <td className="p-3 text-white/70">{p.category}</td>
                                <td className="p-3 text-white/70 uppercase text-xs">{p.part_type}</td>
                                <td className="p-3 text-amber-400">{formatPrice(p.price)}</td>
                                <td className="p-3 text-white/70">{p.stock}</td>
                                <td className="p-3">
                                    <button
                                        onClick={() => setQuickFitment({ id: p.id, name: p.name, compatibility: p.compatibility || [] })}
                                        data-testid="admin-product-quick-fitment"
                                        className="text-xs px-2 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-200 hover:bg-amber-500/25 inline-flex items-center gap-1"
                                        title="Quick edit fitment"
                                    >
                                        <Wrench className="w-3 h-3" />
                                        {(p.compatibility || []).length || 'none'}
                                    </button>
                                </td>
                                <td className="p-3">
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-1.5 rounded hover:bg-white/10" data-testid="admin-product-edit"><Edit className="w-4 h-4" /></button>
                                        <button onClick={() => del(p.id)} className="p-1.5 rounded hover:bg-rose-500/15" data-testid="admin-product-delete"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {showForm && <ProductForm initial={editing} onClose={() => { setShowForm(false); load(); }} />}

            {quickImage && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setQuickImage(null)}>
                    <div className="rrm-panel max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display text-xl text-white">QUICK EDIT IMAGE</h3>
                            <button onClick={() => setQuickImage(null)} className="text-white/60"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="text-sm text-white/70 mb-3 line-clamp-1">{quickImage.name}</div>
                        <ImageInput
                            label="Image"
                            value={quickImage.url}
                            onChange={(v) => setQuickImage({ ...quickImage, url: v })}
                            testid="admin-product-quick-image-input"
                        />
                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={() => setQuickImage(null)} className="px-4 py-2 rounded-md bg-white/5 border border-white/10 text-sm">Cancel</button>
                            <button onClick={saveQuickImage} data-testid="admin-product-quick-image-save" className="px-4 py-2 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white text-sm">Save</button>
                        </div>
                    </div>
                </div>
            )}

            {quickFitment && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setQuickFitment(null)}>
                    <div className="rrm-panel max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display text-xl text-white inline-flex items-center gap-2"><Wrench className="w-5 h-5 text-amber-400" /> QUICK EDIT FITMENT</h3>
                            <button onClick={() => setQuickFitment(null)} className="text-white/60"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="text-sm text-white/70 mb-4 line-clamp-1">{quickFitment.name}</div>
                        <FitmentEditor
                            value={quickFitment.compatibility}
                            onChange={(v) => setQuickFitment({ ...quickFitment, compatibility: v })}
                        />
                        <div className="mt-5 flex justify-end gap-2 sticky bottom-0 bg-[var(--rrm-panel)] pt-3">
                            <button onClick={() => setQuickFitment(null)} className="px-4 py-2 rounded-md bg-white/5 border border-white/10 text-sm">Cancel</button>
                            <button onClick={saveQuickFitment} data-testid="admin-product-quick-fitment-save" className="px-4 py-2 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white text-sm">Save fitment</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ProductForm({ initial, onClose }) {
    const [form, setForm] = useState(
        initial || {
            name: '', description: '', category: 'Brakes', price: 0, stock: 0,
            image_url: '', sku: '', part_type: 'upgrade', brand: '', vin_verified: false,
            fitment_notes: '', compatibility: [],
        }
    );
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form, price: Number(form.price), stock: Number(form.stock) };
            if (initial) {
                await api.patch(`/products/${initial.id}`, payload);
                toast.success('Updated');
            } else {
                await api.post('/products', payload);
                toast.success('Created');
            }
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="rrm-panel max-w-2xl w-full p-6 overflow-y-auto max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-2xl text-white">{initial ? 'EDIT PRODUCT' : 'NEW PRODUCT'}</h2>
                    <button onClick={onClose} className="text-white/60"><X className="w-5 h-5" /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <F label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} />
                    <F label="SKU" v={form.sku} on={(v) => setForm({ ...form, sku: v })} />
                    <F label="Brand" v={form.brand} on={(v) => setForm({ ...form, brand: v })} />
                    <F label="Category" v={form.category} on={(v) => setForm({ ...form, category: v })} />
                    <F label="Price" v={form.price} on={(v) => setForm({ ...form, price: v })} type="number" />
                    <F label="Stock" v={form.stock} on={(v) => setForm({ ...form, stock: v })} type="number" />
                    <div className="sm:col-span-2">
                        <ImageInput
                            label="Product image"
                            value={form.image_url}
                            onChange={(v) => setForm({ ...form, image_url: v })}
                            testid="admin-product-image"
                        />
                    </div>
                    <Sel label="Part Type" v={form.part_type} on={(v) => setForm({ ...form, part_type: v })} options={['oem', 'upgrade']} />
                    <label className="flex items-center gap-2 text-sm text-white/70 mt-6">
                        <input type="checkbox" checked={form.vin_verified} onChange={(e) => setForm({ ...form, vin_verified: e.target.checked })} className="accent-amber-500" /> VIN verified
                    </label>
                    <div className="sm:col-span-2">
                        <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Description</div>
                        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div className="sm:col-span-2">
                        <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Fitment notes</div>
                        <textarea value={form.fitment_notes || ''} onChange={(e) => setForm({ ...form, fitment_notes: e.target.value })} rows={2} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div className="sm:col-span-2 border-t border-white/10 pt-4">
                        <FitmentEditor
                            value={form.compatibility || []}
                            onChange={(v) => setForm({ ...form, compatibility: v })}
                        />
                    </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-md bg-white/5 border border-white/10 text-sm">Cancel</button>
                    <button onClick={save} disabled={saving} data-testid="admin-product-save" className="px-4 py-2 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white text-sm disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
                </div>
            </div>
        </div>
    );
}

function F({ label, v, on, type = 'text' }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
            <input type={type} value={v} onChange={(e) => on(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
        </div>
    );
}
function Sel({ label, v, on, options }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
            <select value={v} onChange={(e) => on(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm">
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}

function OrdersTab() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get('/orders').then(({ data }) => setItems(data.items || [])); }, []);
    return (
        <div className="rrm-panel overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="text-left text-white/50 text-xs uppercase tracking-widest">
                    <tr className="border-b border-white/10">
                        <th className="p-3">Created</th>
                        <th className="p-3">Email</th>
                        <th className="p-3">Items</th>
                        <th className="p-3">Amount</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">Session</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((o) => (
                        <tr key={o.id} className="border-b border-white/5">
                            <td className="p-3 text-white/70">{new Date(o.created_at).toLocaleString()}</td>
                            <td className="p-3 text-white">{o.customer_email || '—'}</td>
                            <td className="p-3 text-white/70">{o.items?.length || 0}</td>
                            <td className="p-3 text-amber-400">{formatPrice(o.amount)}</td>
                            <td className="p-3 text-emerald-300 uppercase text-xs">{o.status}</td>
                            <td className="p-3 font-mono text-xs text-white/40">{o.session_id}</td>
                        </tr>
                    ))}
                    {items.length === 0 && (
                        <tr><td className="p-6 text-center text-white/50" colSpan={6}>No orders yet.</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function MembersTab() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get('/members').then(({ data }) => setItems(data.items || [])); }, []);
    return (
        <div className="rrm-panel overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="text-left text-white/50 text-xs uppercase tracking-widest">
                    <tr className="border-b border-white/10">
                        <th className="p-3">Username</th>
                        <th className="p-3">Email</th>
                        <th className="p-3">Member</th>
                        <th className="p-3">Admin</th>
                        <th className="p-3">Joined</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((u) => (
                        <tr key={u.id} className="border-b border-white/5">
                            <td className="p-3 text-white">{u.username}</td>
                            <td className="p-3 text-white">{u.email}</td>
                            <td className="p-3">{u.is_member ? <span className="text-emerald-300 text-xs">YES</span> : <span className="text-white/40 text-xs">NO</span>}</td>
                            <td className="p-3">{u.is_admin ? <span className="text-amber-300 text-xs">YES</span> : <span className="text-white/40 text-xs">NO</span>}</td>
                            <td className="p-3 text-white/60">{new Date(u.created_at).toLocaleDateString()}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function NewsletterTab() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get('/newsletter/subscribers').then(({ data }) => setItems(data.items || [])); }, []);
    return (
        <div className="rrm-panel p-4">
            <div className="text-sm text-white/60 mb-3">{items.length} subscribers</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[60vh] overflow-y-auto">
                {items.map((s, i) => (
                    <div key={s.email || `nl-${i}`} className="text-sm text-white/80 bg-white/5 border border-white/10 rounded-md px-3 py-2">
                        {s.email}
                    </div>
                ))}
            </div>
        </div>
    );
}



// =====================================================================
// SITE CONTENT TAB
// =====================================================================
const SITE_FIELDS = [
    { group: 'Hero (top of homepage)', fields: [
        { key: 'hero_eyebrow', label: 'Hero badge text', type: 'text' },
        { key: 'hero_title_line1', label: 'Hero title — line 1', type: 'text' },
        { key: 'hero_title_line2', label: 'Hero title — line 2 (red)', type: 'text' },
        { key: 'hero_subtitle', label: 'Hero subtitle', type: 'textarea' },
        { key: 'hero_image_url', label: 'Hero image', type: 'image' },
        { key: 'hero_image_alt', label: 'Hero image alt-text (accessibility)', type: 'text' },
    ]},
    { group: 'Featured platform card (overlays hero image)', fields: [
        { key: 'featured_eyebrow', label: 'Eyebrow', type: 'text' },
        { key: 'featured_title', label: 'Platform title (match the hero image)', type: 'text' },
    ]},
    { group: 'AI Mechanic section', fields: [
        { key: 'ai_section_eyebrow', label: 'Eyebrow', type: 'text' },
        { key: 'ai_section_title', label: 'Title', type: 'text' },
        { key: 'ai_section_body', label: 'Body copy', type: 'textarea' },
        { key: 'ai_section_image_url', label: 'Section image', type: 'image' },
    ]},
    { group: 'Footer / About / Join', fields: [
        { key: 'footer_about', label: 'Footer about copy', type: 'textarea' },
        { key: 'join_section_title', label: 'Join page title', type: 'text' },
        { key: 'join_section_body', label: 'Join page body', type: 'textarea' },
    ]},
];

function SiteContentTab() {
    const [content, setContent] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => { api.get('/site/content').then(({ data }) => setContent(data)); }, []);
    if (!content) return <div className="text-white/50">Loading…</div>;
    const setField = (k, v) => setContent({ ...content, [k]: v });
    const save = async () => {
        setSaving(true);
        try { await api.put('/admin/site/content', content); toast.success('Site content saved'); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
        finally { setSaving(false); }
    };
    return (
        <div className="space-y-6">
            <div className="text-sm text-white/60">Edit the copy and images shown on the homepage and across the site. Hit Save when done.</div>
            {SITE_FIELDS.map((g) => (
                <section key={g.group} className="rrm-panel p-5 space-y-3">
                    <h3 className="font-display text-lg text-white">{g.group}</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        {g.fields.map((f) => (
                            <div key={f.key} className={f.type === 'image' || f.type === 'textarea' ? 'md:col-span-2' : ''}>
                                {f.type === 'image' ? (
                                    <ImageInput label={f.label} value={content[f.key]} onChange={(v) => setField(f.key, v)} testid={`site-${f.key}`} />
                                ) : (
                                    <>
                                        <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{f.label}</div>
                                        {f.type === 'textarea' ? (
                                            <textarea value={content[f.key] || ''} onChange={(e) => setField(f.key, e.target.value)} rows={3} data-testid={`site-${f.key}`} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                                        ) : (
                                            <input value={content[f.key] || ''} onChange={(e) => setField(f.key, e.target.value)} data-testid={`site-${f.key}`} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            ))}
            <button onClick={save} disabled={saving} data-testid="site-content-save" className="px-5 py-2.5 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white font-semibold inline-flex items-center gap-2">
                {saving ? <Save className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save site content
            </button>
        </div>
    );
}


// =====================================================================
// VEHICLES TAB
// =====================================================================
function VehiclesTab() {
    const [catalog, setCatalog] = useState({});
    const [form, setForm] = useState({ make: '', model: '', years: '', trims: '' });
    const [saving, setSaving] = useState(false);
    const load = () => api.get('/vehicles').then(({ data }) => setCatalog(data || {}));
    useEffect(() => { load(); }, []);

    const add = async (e) => {
        e.preventDefault();
        const yearsArr = form.years.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean).map(Number).filter(Number.isFinite);
        const trimsArr = form.trims.split(',').map((s) => s.trim()).filter(Boolean);
        if (!form.make || !form.model) { toast.error('Make and model are required'); return; }
        setSaving(true);
        try {
            await api.put('/admin/vehicles', { make: form.make, model: form.model, years: yearsArr, trims: trimsArr });
            toast.success(`${form.make} ${form.model} saved`);
            setForm({ make: '', model: '', years: '', trims: '' });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed');
        } finally { setSaving(false); }
    };

    const removeModel = async (make, model) => {
        if (!confirm(`Remove ${make} ${model} from the catalog?`)) return;
        try { await api.delete(`/admin/vehicles?make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}`); toast.success('Removed'); load(); }
        catch { toast.error('Failed'); }
    };

    return (
        <div className="space-y-6">
            <section className="rrm-panel p-5">
                <h3 className="font-display text-lg text-white mb-3">ADD / EXTEND A VEHICLE</h3>
                <form onSubmit={add} className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Make</div>
                        <input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} placeholder="Chevrolet" data-testid="vehicles-make-input" className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Model</div>
                        <input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Camaro" data-testid="vehicles-model-input" className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Years (comma)</div>
                        <input value={form.years} onChange={(e) => setForm({ ...form, years: e.target.value })} placeholder="2020,2021,2022,2023" data-testid="vehicles-years-input" className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Trims (comma)</div>
                        <input value={form.trims} onChange={(e) => setForm({ ...form, trims: e.target.value })} placeholder="SS, ZL1, 1LE" data-testid="vehicles-trims-input" className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div className="lg:col-span-4">
                        <button type="submit" disabled={saving} data-testid="vehicles-add-submit" className="px-4 py-2 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white text-sm inline-flex items-center gap-2 disabled:opacity-50">
                            <Plus className="w-4 h-4" /> Save vehicle
                        </button>
                        <span className="ml-3 text-xs text-white/50">Existing make+model? New years/trims will be merged with the current list.</span>
                    </div>
                </form>
            </section>

            <section className="space-y-4">
                {Object.entries(catalog).map(([make, models]) => (
                    <div key={make} className="rrm-panel p-5">
                        <div className="font-display text-xl text-white mb-3">{make}</div>
                        <div className="grid md:grid-cols-2 gap-3">
                            {Object.entries(models).map(([model, info]) => (
                                <div key={model} className="bg-white/5 border border-white/10 rounded-md p-3">
                                    <div className="flex items-center justify-between">
                                        <div className="font-semibold text-white">{model}</div>
                                        <button onClick={() => removeModel(make, model)} className="text-rose-300 text-xs hover:underline">Remove</button>
                                    </div>
                                    <div className="text-xs text-white/50 mt-1">
                                        Years: <span className="text-white/70 font-mono">{(info.years || []).join(', ') || '—'}</span>
                                    </div>
                                    <div className="text-xs text-white/50 mt-1">
                                        Trims: <span className="text-white/70">{(info.trims || []).join(' · ') || '—'}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </section>
        </div>
    );
}


// =====================================================================
// SETTINGS TAB (admin credentials + Stripe key)
// =====================================================================
function SettingsTab() {
    return (
        <div className="space-y-6">
            <CredentialsCard />
            <StripeKeyCard />
        </div>
    );
}

function CredentialsCard() {
    const [form, setForm] = useState({ current_password: '', new_username: '', new_email: '', new_password: '', new_password_confirm: '' });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!form.current_password) { toast.error('Enter your current password to confirm'); return; }
        if (form.new_password && form.new_password !== form.new_password_confirm) { toast.error('New passwords do not match'); return; }
        const payload = { current_password: form.current_password };
        if (form.new_username) payload.new_username = form.new_username;
        if (form.new_email) payload.new_email = form.new_email;
        if (form.new_password) payload.new_password = form.new_password;
        if (Object.keys(payload).length === 1) { toast.error('Nothing to update'); return; }
        setBusy(true);
        try {
            const { data } = await api.patch('/admin/credentials', payload);
            toast.success(`Updated: ${(data.changed || []).join(', ') || 'nothing'}`);
            setForm({ current_password: '', new_username: '', new_email: '', new_password: '', new_password_confirm: '' });
        } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
        finally { setBusy(false); }
    };
    return (
        <section className="rrm-panel p-5">
            <h3 className="font-display text-lg text-white inline-flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-400" /> ADMIN SIGN-IN</h3>
            <p className="text-xs text-white/50 mt-1">Change your admin username, email, and password.</p>
            <form onSubmit={submit} className="mt-4 grid md:grid-cols-2 gap-3">
                <PwField label="Current password (required)" value={form.current_password} onChange={(v) => setForm({ ...form, current_password: v })} testid="settings-current-password" required />
                <div></div>
                <Txt label="New username" value={form.new_username} onChange={(v) => setForm({ ...form, new_username: v })} testid="settings-new-username" />
                <Txt label="New email" value={form.new_email} onChange={(v) => setForm({ ...form, new_email: v })} testid="settings-new-email" type="email" />
                <PwField label="New password" value={form.new_password} onChange={(v) => setForm({ ...form, new_password: v })} testid="settings-new-password" />
                <PwField label="Confirm new password" value={form.new_password_confirm} onChange={(v) => setForm({ ...form, new_password_confirm: v })} testid="settings-new-password-confirm" />
                <div className="md:col-span-2">
                    <button disabled={busy} type="submit" data-testid="settings-credentials-save" className="px-4 py-2 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white text-sm font-semibold disabled:opacity-50">
                        Update credentials
                    </button>
                </div>
            </form>
        </section>
    );
}

function StripeKeyCard() {
    const [status, setStatus] = useState(null);
    const [newKey, setNewKey] = useState('');
    const [busy, setBusy] = useState(false);
    const load = () => api.get('/admin/stripe-key').then(({ data }) => setStatus(data));
    useEffect(() => { load(); }, []);
    const save = async (e) => {
        e.preventDefault();
        if (!/^sk_(test|live)_/.test(newKey)) { toast.error('Enter a valid Stripe secret key (sk_test_… or sk_live_…)'); return; }
        setBusy(true);
        try { await api.put('/admin/stripe-key', { api_key: newKey }); toast.success('Stripe key saved'); setNewKey(''); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        if (!confirm('Remove the Stripe key? Checkout will be disabled until set again.')) return;
        await api.delete('/admin/stripe-key'); toast.success('Stripe key removed'); load();
    };
    return (
        <section className="rrm-panel p-5">
            <h3 className="font-display text-lg text-white inline-flex items-center gap-2"><CreditCard className="w-5 h-5 text-amber-400" /> STRIPE API KEY</h3>
            <p className="text-xs text-white/50 mt-1">Add your <strong>live</strong> Stripe secret key when you're ready to take real payments. Use a test key (sk_test_…) for development.</p>
            <div className="mt-3 rrm-panel p-3 flex items-center justify-between gap-2 bg-black/30">
                <div>
                    <div className="text-xs uppercase tracking-widest text-white/50">Current key</div>
                    <div className="text-sm font-mono text-white">{status?.configured ? `${status.mode.toUpperCase()} · ${status.masked}` : 'Not configured'}</div>
                </div>
                {status?.configured && (
                    <button onClick={remove} className="text-rose-300 text-xs hover:underline">Remove</button>
                )}
            </div>
            <form onSubmit={save} className="mt-4 grid md:grid-cols-[1fr_auto] gap-3">
                <input
                    type="password"
                    placeholder="sk_live_… or sk_test_…"
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    data-testid="settings-stripe-key-input"
                    className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                />
                <button disabled={busy} type="submit" data-testid="settings-stripe-key-save" className="px-4 py-2 rounded-md bg-amber-500/20 border border-amber-500/40 text-amber-200 text-sm font-semibold disabled:opacity-50">
                    Save Stripe key
                </button>
            </form>
            <div className="mt-3 text-[11px] text-white/40">
                Where to find it: Stripe Dashboard → Developers → API keys → "Secret key". The live key starts with <span className="font-mono">sk_live_</span>.
            </div>
        </section>
    );
}

function Txt({ label, value, onChange, testid, type = 'text' }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}</div>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
        </div>
    );
}
function PwField({ label, value, onChange, testid, required }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{label}{required && <span className="text-rose-300 ml-0.5">*</span>}</div>
            <input type="password" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
        </div>
    );
}
